import React, { useCallback, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AlertCircle, PanelLeftClose, PanelLeftOpen, Users, Network } from 'lucide-react';
import { useWorkspaceData } from './AccountsPeopleTabs';
import AccountsContactsDirectory from './AccountsContactsDirectory';
import OrgChart from '../AccountWorkspace/OrgChart';
import AddContactModal from '../AccountWorkspace/AddContactModal';
import '../AccountWorkspace/AccountWorkspace.css';
import './peopleSplit.css';

/*
 * AccountsPeopleSplit — the People tab.
 *
 * WHY THIS REPLACED A BOARD. People was a widget board whose shipped order put
 * the org chart FIRST and the directory under it. The chart is a pan/zoom
 * canvas, so reaching the directory meant scrolling past a canvas that eats
 * the wheel — Ryan, 2026-09-18: "scrolling past the org chart without zooming
 * in to get to the directory is stupid, it doesn't work". Stacking a canvas
 * above a list cannot be fixed by reordering the stack, because whichever one
 * is on top owns the scroll.
 *
 * So they sit SIDE BY SIDE and neither is reached through the other. The
 * directory is the left rail — the thing you came to use, searchable from the
 * first keystroke — and the chart is the stage beside it. Either can be
 * collapsed to give the other the whole width.
 *
 * They are two views of ONE roster, and the hinge is per-row: "Show in the org
 * chart" focuses that person on the canvas (the chart's own gesture — isolate
 * their branch plus the ancestors that give it context, then re-fit). The
 * directory answers "where is this person", the chart answers "who do they
 * report to", and the question is usually asked in that order.
 *
 * ONE fetch behind both: the same `['workspace', …]` key the chart, the
 * Meetings tab and AccountWorkspace use, so an edit in either pane reconciles
 * in all of them.
 */

/** Wide enough for two useful panes. Below it they become one pane + a switch. */
const SPLIT_MIN_PX = 900;

export default function AccountsPeopleSplit({ selectedDepartment }) {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useWorkspaceData(selectedDepartment);
  const [addManager, setAddManager] = useState(undefined); // undefined = closed
  const [collapsed, setCollapsed] = useState(null); // null | 'directory' | 'chart'
  // Which pane a NARROW screen is showing. Separate from `collapsed`: on a
  // phone there is no "both", so a collapse toggle would have nothing to mean.
  const [narrowPane, setNarrowPane] = useState('directory');
  const [wide, setWide] = useState(
    () => (typeof window === 'undefined' ? true : window.innerWidth >= SPLIT_MIN_PX),
  );
  const [focusRequest, setFocusRequest] = useState(null);
  const nonceRef = useRef(0);

  React.useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onResize = () => setWide(window.innerWidth >= SPLIT_MIN_PX);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const showInChart = useCallback((contact) => {
    if (!contact || !contact.id) return;
    nonceRef.current += 1;
    setFocusRequest({ id: String(contact.id), nonce: nonceRef.current });
    // Asking to see someone in the chart is also asking for the chart to BE
    // visible — on a narrow screen it is behind a switch, and collapsed it is
    // a 12px rail.
    setNarrowPane('chart');
    setCollapsed((cur) => (cur === 'chart' ? null : cur));
  }, []);

  if (isLoading) return <div className="accounts-panel-note">Loading people…</div>;
  if (error) {
    return (
      <div className="accounts-error">
        <AlertCircle size={16} /> {error.message || 'Failed to load contacts'}
      </div>
    );
  }

  const contacts = data?.contacts || [];
  const showDirectory = wide ? collapsed !== 'directory' : narrowPane === 'directory';
  const showChart = wide ? collapsed !== 'chart' : narrowPane === 'chart';

  const directory = (
    <AccountsContactsDirectory
      selectedDepartment={selectedDepartment}
      onShowInChart={showInChart}
    />
  );

  const chart = (
    <OrgChart
      contacts={contacts}
      accountId={data?.account?.id}
      departmentId={selectedDepartment?.department_id}
      departmentLabel={selectedDepartment?.acronym || selectedDepartment?.official_name}
      onChanged={() => queryClient.invalidateQueries({ queryKey: ['workspace'] })}
      onAddContact={(manager) => setAddManager(manager)}
      focusRequest={focusRequest}
      // The directory in the next pane IS the company roster. See OrgChart's
      // `rosterScope`.
      rosterScope="off-account"
    />
  );

  return (
    <div className={`aps${wide ? '' : ' aps--narrow'}`}>
      {!wide && (
        // A narrow screen gets a switch, not a collapse: there is no "both" to
        // collapse FROM, and a 12px rail on a phone is a hit target nobody
        // finds.
        <div className="aps-switch" role="tablist" aria-label="People view">
          <button
            type="button" role="tab" aria-selected={narrowPane === 'directory'}
            className={`aps-switch-btn${narrowPane === 'directory' ? ' is-on' : ''}`}
            onClick={() => setNarrowPane('directory')}
          >
            <Users size={14} /> Directory
          </button>
          <button
            type="button" role="tab" aria-selected={narrowPane === 'chart'}
            className={`aps-switch-btn${narrowPane === 'chart' ? ' is-on' : ''}`}
            onClick={() => setNarrowPane('chart')}
          >
            <Network size={14} /> Org chart
          </button>
        </div>
      )}

      <div className="aps-panes">
        {showDirectory && (
          <section className="aps-pane aps-pane--directory" aria-label="Contacts directory">
            {directory}
          </section>
        )}

        {wide && (
          // The collapse control lives ON the seam, so it reads as belonging to
          // both panes rather than to whichever one it sits inside.
          <div className="aps-seam">
            {/* The controls stick; the seam stretches. See peopleSplit.css —
                sticking the seam itself shrank the divider between the panes
                to the height of these two buttons. */}
            <div className="aps-seam-controls">
              <button
                type="button"
                className="aps-collapse"
                onClick={() => setCollapsed((cur) => (cur === 'directory' ? null : 'directory'))}
                aria-label={collapsed === 'directory' ? 'Show the directory' : 'Collapse the directory'}
                title={collapsed === 'directory' ? 'Show the directory' : 'Collapse the directory'}
              >
                {collapsed === 'directory' ? <PanelLeftOpen size={14} /> : <PanelLeftClose size={14} />}
              </button>
              <button
                type="button"
                className="aps-collapse"
                onClick={() => setCollapsed((cur) => (cur === 'chart' ? null : 'chart'))}
                aria-label={collapsed === 'chart' ? 'Show the org chart' : 'Collapse the org chart'}
                title={collapsed === 'chart' ? 'Show the org chart' : 'Collapse the org chart'}
              >
                {collapsed === 'chart' ? <PanelLeftClose size={14} /> : <PanelLeftOpen size={14} />}
              </button>
            </div>
          </div>
        )}

        {showChart && (
          <section className="aps-pane aps-pane--chart" aria-label="Org chart">
            {chart}
          </section>
        )}
      </div>

      {addManager !== undefined && (
        <AddContactModal
          manager={addManager}
          accountId={data?.account?.id}
          departmentId={selectedDepartment?.department_id}
          onClose={() => setAddManager(undefined)}
          onSaved={() => {
            setAddManager(undefined);
            queryClient.invalidateQueries({ queryKey: ['workspace'] });
          }}
        />
      )}
    </div>
  );
}
